import type { AuthedPlayer } from "./types";
import { uuid } from "./uuid";

// Device id: one per physical device, shared across every org it ever joins
// (not org-keyed) — matches legacy static/js/workflow.js's DEVICE_ID.
const DEVICE_ID_KEY = "trim.deviceId";
export function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = uuid();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

// Which org this device last authenticated into — a small local pointer so
// boot-time code knows which `trim.<org>.*` namespace to read before it has
// decoded any token. Purely client bookkeeping; never sent to the server.
const CURRENT_ORG_KEY = "trim.currentOrg";
export function getCurrentOrg(): number | null {
  const raw = localStorage.getItem(CURRENT_ORG_KEY);
  const n = raw ? Number(raw) : NaN;
  return Number.isInteger(n) ? n : null;
}

function orgKey(orgId: number, key: string): string {
  return `trim.${orgId}.${key}`;
}

// Org-keyed auth: same rationale as legacy auth.js's ORG-scoped storage keys
// ("one device touching two workspaces can never mix tokens").
export function getAuthToken(orgId: number): string | null {
  return localStorage.getItem(orgKey(orgId, "authToken"));
}
export function getAuthPlayer(orgId: number): AuthedPlayer | null {
  try {
    return JSON.parse(localStorage.getItem(orgKey(orgId, "authPlayer")) || "null");
  } catch {
    return null;
  }
}
export function setAuth(orgId: number, token: string, player: AuthedPlayer): void {
  localStorage.setItem(CURRENT_ORG_KEY, String(orgId));
  localStorage.setItem(orgKey(orgId, "authToken"), token);
  localStorage.setItem(orgKey(orgId, "authPlayer"), JSON.stringify(player));
}
export function clearAuth(orgId: number): void {
  localStorage.removeItem(orgKey(orgId, "authToken"));
  localStorage.removeItem(orgKey(orgId, "authPlayer"));
}

// Trim style / nug size chips persist per DEVICE, not per org — a crew
// usually runs the same style all day regardless of which workspace signed
// them in (matches legacy's non-namespaced trim.trimType/trim.nugSize).
export function getTrimType(): string | null {
  return localStorage.getItem("trim.trimType");
}
export function setTrimType(v: string): void {
  localStorage.setItem("trim.trimType", v);
}
export function getNugSize(): string | null {
  return localStorage.getItem("trim.nugSize");
}
export function setNugSize(v: string): void {
  localStorage.setItem("trim.nugSize", v);
}

// Last-picked strain IS org-keyed — a strain id is meaningless outside the
// org it belongs to.
export function getLastStrainId(orgId: number): number | null {
  const raw = localStorage.getItem(orgKey(orgId, "lastStrainId"));
  const n = raw ? Number(raw) : NaN;
  return Number.isInteger(n) ? n : null;
}
export function setLastStrainId(orgId: number, strainId: number): void {
  localStorage.setItem(orgKey(orgId, "lastStrainId"), String(strainId));
}

// One-shot hand-off from the home screen's Quick/Full mode cards to
// TrimPage's initial step machine — sessionStorage (not localStorage) since
// it's only meant to survive the single navigation to /trim, and read-once
// (take, not get) so a later fresh visit to /trim (e.g. a bookmark, or the
// tab reopening) falls back to "quick" rather than replaying a stale choice.
const PENDING_MODE_KEY = "trim.pendingMode";
export function setPendingMode(mode: "quick" | "full"): void {
  sessionStorage.setItem(PENDING_MODE_KEY, mode);
}
export function takePendingMode(): "quick" | "full" {
  const v = sessionStorage.getItem(PENDING_MODE_KEY);
  sessionStorage.removeItem(PENDING_MODE_KEY);
  return v === "full" ? "full" : "quick";
}

// Scale-cam rate unit — device-level like trimType/nugSize above, matches
// legacy live-scale.js's un-namespaced trim.rateUnit key.
export type RateUnit = "gpm" | "ozhr" | "lbhr";
export function getRateUnit(): RateUnit {
  const v = localStorage.getItem("trim.rateUnit");
  return v === "ozhr" || v === "lbhr" ? v : "gpm";
}
export function setRateUnit(v: RateUnit): void {
  localStorage.setItem("trim.rateUnit", v);
}

// Full-batch photo ids captured so far, keyed by the run's client_uuid — lets
// a page reload mid-trim (after a start/end/QA photo was already captured)
// restore which photo ids the eventual save should link, since the live
// session row itself has nowhere to carry them (see runs-routes.ts CHECK
// note: v5 has no photo columns at all, only the sync-event payload does).
type PhotoWhich = "start" | "end" | "qa";
export function getFlowPhotoIds(clientUuid: string): Partial<Record<PhotoWhich, string>> {
  try {
    return JSON.parse(localStorage.getItem(`trim.photoIds.${clientUuid}`) || "{}");
  } catch {
    return {};
  }
}
export function setFlowPhotoId(clientUuid: string, which: PhotoWhich, photoId: string): void {
  const cur = getFlowPhotoIds(clientUuid);
  cur[which] = photoId;
  localStorage.setItem(`trim.photoIds.${clientUuid}`, JSON.stringify(cur));
}
export function clearFlowPhotoIds(clientUuid: string): void {
  localStorage.removeItem(`trim.photoIds.${clientUuid}`);
}

export type SyncCursor = { after_received_at: number; after_event_id: string };
export function getSyncCursor(orgId: number): SyncCursor {
  try {
    const raw = localStorage.getItem(orgKey(orgId, "syncCursor"));
    if (raw) return JSON.parse(raw) as SyncCursor;
  } catch {
    /* ignore */
  }
  return { after_received_at: 0, after_event_id: "" };
}
export function setSyncCursor(orgId: number, cursor: SyncCursor): void {
  localStorage.setItem(orgKey(orgId, "syncCursor"), JSON.stringify(cursor));
}
